import {
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ApiUser } from '../../types';

const { Title } = Typography;
const { Option } = Select;

const ReassignLeadModal = ({ visible, onCancel, lead, onAssignSubmit, message, hasPermission }: any) => {
  const [form] = Form.useForm();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(false);

  const title = `Assign Lead: ${lead?.full_name || ''}`;

  useEffect(() => {
    if (visible && lead) {
      form.resetFields();
      setLoading(true);

      api.get(`leads/users`, { params: { property_area: lead.property_area?.id } })
        .then(response => {
          setUsers(response.data.data || []);
        })
        .catch(error => {
          console.error('Failed to fetch users:', error);
          message.error('Gagal memuat data pengguna untuk area properti ini.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [lead, visible, form, message]);

  const handleOk = () => {
    form.validateFields()
      .then(values => {
        onAssignSubmit(lead.id, values.assigned_to_id);
        form.resetFields();
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <Modal
      title={title}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="assigned_to_id"
          label="Assign kepada"
          rules={[{ required: true, message: 'Silakan pilih pengguna!' }]}
        >
          <Select
            showSearch
            placeholder="Pilih sales agent"
            loading={loading}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>{user.full_name}</Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const UnassignedLeadDetail = () => {
  const { hasPermission } = useAuth();
  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [reassignModalVisible, setReassignModalVisible] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const { message } = App.useApp();

  const fetchLead = useCallback(async () => {
    try {
      const response = await api.get(`leads/${id}`);
      setLead(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch lead:', error);
      message.error('Gagal memuat data lead');
      navigate('/leads/unassigned');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, message]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const handleAssignSubmit = async (leadId: string, assignedToId: string) => {
    try {
      await api.post(`leads/${leadId}/assign`, { assigned_to_id: assignedToId });
      message.success('Lead berhasil di-assign');
      setReassignModalVisible(false);
      navigate('/leads/unassigned'); // Go back to the list after assigning
    } catch (error: any) {
      console.error('Failed to assign lead:', error);
      message.error(error.response?.data?.message || 'Gagal meng-assign lead');
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      low: 'green',
      medium: 'orange',
      high: 'red',
    };
    return colors[priority] || 'default';
  };

  const getFollowUpStatusColor = (status: string) => {
    if (!status) return 'default';
    const statusName = status.toLowerCase();
    if (statusName === 'hot') return 'error';
    if (statusName === 'rsv') return 'green';
    if (statusName === 'cold') return 'processing';
    return 'default';
  };

  const renderInterestStatusTag = (interestStatus: string) => {
    if (!interestStatus) return <Tag>-</Tag>;
    const formatToTitleCase = (str: string) => str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const colorMap: { [key: string]: string } = {
      minat_pesan: 'success',
      minat_proses: 'blue',
      minat_tidak_pesan: 'warning',
      tidak_minat: 'error',
    };
    return <Tag color={colorMap[interestStatus] || 'default'}>{formatToTitleCase(interestStatus)}</Tag>;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  if (!lead) {
    return <div>Lead tidak ditemukan</div>;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/leads/unassigned')} style={{ marginRight: 16 }}>
            Kembali
          </Button>
          <Title level={2} style={{ margin: 0, display: 'inline' }}>
            {lead.full_name}
          </Title>
          <Tag color={getFollowUpStatusColor(lead.status_lead?.name)} style={{ marginLeft: 16 }}>
            {lead.status_lead?.name}
          </Tag>
        </Col>
        <Col>
          {hasPermission('lead.assign') && (
            <Button
              type="primary"
              icon={<UserSwitchOutlined />}
              onClick={() => setReassignModalVisible(true)}
            >
              Assign to Sales
            </Button>
          )}
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Informasi Lead">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Nama Lengkap">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
                  {lead.full_name}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {lead.email ? <a href={`mailto:${lead.email}`}><MailOutlined style={{ marginRight: 4 }} />{lead.email}</a> : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Telepon">
                {lead.phone ? <a href={`tel:${lead.phone}`}><PhoneOutlined style={{ marginRight: 4 }} />{lead.phone}</a> : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Alamat">{lead.alamat || '-'}</Descriptions.Item>
              <Descriptions.Item label="Tipe Properti"><Tag>{lead.property_type?.name}</Tag></Descriptions.Item>
              <Descriptions.Item label="Property Area"><Tag color="cyan">{lead.property_area?.name || 'N/A'}</Tag></Descriptions.Item>
              <Descriptions.Item label="Budget">{formatCurrency(lead.budget)}</Descriptions.Item>
              <Descriptions.Item label="Prioritas"><Tag color={getPriorityColor(lead.lead_priority?.name)}>{lead.lead_priority?.name?.toUpperCase()}</Tag></Descriptions.Item>
              <Descriptions.Item label="Status Ketertarikan">{renderInterestStatusTag(lead.interest_status)}</Descriptions.Item>
              <Descriptions.Item label="Sumber Lead"><Tag color="blue">{lead.lead_source?.name}</Tag></Descriptions.Item>
              {lead.lead_source?.name === 'Referral' && lead.referal && <Descriptions.Item label="Nama Referral">{lead.referal}</Descriptions.Item>}
              <Descriptions.Item label="Created By">{lead.created_by?.full_name}</Descriptions.Item>
              <Descriptions.Item label="Tanggal Dibuat">{dayjs(lead.created_at).format('DD MMM YYYY, HH:mm')}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Quick Contact">
            <Button
              type="primary"
              icon={<PhoneOutlined />}
              size="large"
              block
              href={`https://wa.me/62${lead.phone}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ backgroundColor: '#25D366', borderColor: '#25D366', marginBottom: '12px' }}
            >
              Hubungi via WhatsApp
            </Button>
            <Button icon={<MailOutlined />} size="large" block href={`mailto:${lead.email}`}>
              Kirim Email
            </Button>
          </Card>
        </Col>
      </Row>

      <ReassignLeadModal
        visible={reassignModalVisible}
        onCancel={() => setReassignModalVisible(false)}
        lead={lead}
        onAssignSubmit={handleAssignSubmit}
        message={message}
        hasPermission={hasPermission}
      />
    </div>
  );
};

export default UnassignedLeadDetail;
